import { z } from 'zod';
/**
 * La salida segura de PIA · la lista cerrada de Fundación (E4, PIA-1).
 *
 * **PIA no interpreta: compara.** Las frases que cierran la conversación y le
 * indican al paciente llamar al 107 son las 137 de la planilla de Fundación del
 * 27/10 (`corpus/fundacion-2026-10-27/pia-frases-situaciones-criticas-volcado.csv`),
 * cada una con su ID ("B-001", "A-001"). No se agregan frases ni situaciones:
 * la lista es de Fundación y cambia cuando llega un archivo nuevo de ellos.
 *
 * Este módulo tiene los nombres (situaciones y hojas), la normalización que
 * usan la carga y el comparador, y los contratos de la pestaña "Salida
 * segura" del panel de Fundación.
 */
/** Las seis situaciones críticas de la planilla, en su orden. */
export const situacionCriticaSchema = z.enum([
    'ideacion_suicida',
    'caida_grave',
    'dolor_toracico',
    'confusion_aguda',
    'sangrado_abundante',
    'fiebre_alta_sostenida',
]);
/** Las dos hojas de la planilla. El orden importa: la primera coincidencia gana. */
export const hojaDeFrasesSchema = z.enum(['frases_base', 'vocabulario_adultos_mayores']);
/**
 * El texto en la forma en que se compara: minúsculas, sin tildes, sin signos y
 * con espacios simples. "¿Para qué quiero seguir así?" queda "para que quiero
 * seguir asi".
 *
 * La "ñ" queda como "n" (se descompone igual que una tilde): "año" y "ano" se
 * comparan iguales. Es una pérdida aceptable para comparar frases y es la
 * misma para el mensaje y para la lista, así que nunca separa dos textos que
 * deberían coincidir.
 */
export function normalizarFrase(texto) {
    return texto
        .normalize('NFD')
        .replace(/\p{Diacritic}/gu, '')
        .toLocaleLowerCase('es-AR')
        .replace(/[^a-z0-9]+/g, ' ')
        .trim();
}
const codigoDeFrase = z.string().regex(/^[AB]-\d{3}$/);
/** Una frase de la lista, como la ve Fundación en su panel. */
export const filaDeFraseSchema = z.strictObject({
    codigo: codigoDeFrase,
    hoja: hojaDeFrasesSchema,
    situacion: situacionCriticaSchema,
    tipoDeExpresion: z.string().min(1).max(80),
    frase: z.string().min(1).max(300),
    activa: z.boolean(),
});
/** `GET /api/v1/fundacion/corpus/salida-segura` */
export const listaDeFrasesSchema = z.strictObject({ frases: z.array(filaDeFraseSchema) });
export const parametrosDeFraseSchema = z.strictObject({ codigo: codigoDeFrase });
/** Activar o desactivar: lo único que se puede cambiar de una frase. */
export const activarFraseSchema = z.strictObject({ activa: z.boolean() });
// ─────────────────────────────────────────────────────────────────────────────
// El comparador (PIA-2)
// ─────────────────────────────────────────────────────────────────────────────
/**
 * Las palabras que no cuentan para comparar: artículos, preposiciones,
 * pronombres, "y", "que", "muy" y "mucho" (lista cerrada del prompt de PIA-2).
 * Están normalizadas como la frase: sin tildes ("él" es "el", "sí" es "si").
 *
 * **"no" no está, a propósito**: "no puedo moverme" y "puedo moverme" no son
 * lo mismo. Tampoco los adverbios ("acá", "más", "nunca"): no están en la
 * lista del prompt y sacarlos generalizaría más de lo pedido.
 */
export const PALABRAS_VACIAS = new Set([
    // Artículos
    'el',
    'la',
    'los',
    'las',
    'un',
    'una',
    'unos',
    'unas',
    'lo',
    'al',
    'del',
    // Preposiciones
    'a',
    'ante',
    'bajo',
    'con',
    'contra',
    'de',
    'desde',
    'en',
    'entre',
    'hacia',
    'hasta',
    'para',
    'por',
    'segun',
    'sin',
    'sobre',
    'tras',
    // Pronombres
    'yo',
    'me',
    'mi',
    'mis',
    'conmigo',
    'vos',
    'tu',
    'tus',
    'te',
    'ti',
    'usted',
    'se',
    'si',
    'su',
    'sus',
    'le',
    'les',
    'ella',
    'ellas',
    'ellos',
    'nos',
    'nosotros',
    'nosotras',
    'mio',
    'mia',
    'esto',
    'esta',
    'este',
    'eso',
    'esa',
    'ese',
    'estos',
    'estas',
    'esos',
    'esas',
    'aquello',
    // Conjunciones y cuantificadores nombrados por el prompt
    'y',
    'e',
    'que',
    'muy',
    'mucho',
    'mucha',
    'muchos',
    'muchas',
]);
/** Las palabras con contenido de una frase normalizada, en orden. */
export function palabrasConContenido(normalizada) {
    return normalizada.split(' ').filter((palabra) => palabra && !PALABRAS_VACIAS.has(palabra));
}
/**
 * Si el mensaje del paciente coincide con una frase de Fundación.
 *
 * Coincide si (a) la frase entera está dentro del mensaje, como palabras
 * enteras, o (b) todas las palabras con contenido de la frase están en el
 * mensaje, en cualquier orden y con otras en el medio, y la frase tiene al
 * menos dos palabras con contenido (con una sola, "fiebre" cerraría cualquier
 * conversación que la nombre).
 *
 * **Es una generalización mecánica de las frases de Fundación, no frases
 * nuevas**: no hay sinónimos ni interpretación. Ante la duda coincide: un
 * falso positivo cuesta una conversación cortada con el 107 en pantalla; un
 * falso negativo cuesta más.
 */
export function coincideConLaFrase(mensajeNormalizado, frase) {
    if (!frase.fraseNormalizada)
        return false;
    if (` ${mensajeNormalizado} `.includes(` ${frase.fraseNormalizada} `))
        return true;
    const contenido = palabrasConContenido(frase.fraseNormalizada);
    if (contenido.length < 2)
        return false;
    const delMensaje = new Set(mensajeNormalizado.split(' '));
    return contenido.every((palabra) => delMensaje.has(palabra));
}
const ORDEN_DE_LAS_HOJAS = Object.freeze({
    frases_base: 0,
    vocabulario_adultos_mayores: 1,
});
/**
 * La primera frase activa con la que coincide el mensaje, por hoja (primero
 * las frases base) y por código; o `null`. Recibe las frases ya filtradas por
 * activas: el comparador no decide qué frase vale.
 */
export function buscarSalidaSegura(mensaje, frases) {
    const normalizado = normalizarFrase(mensaje);
    if (!normalizado)
        return null;
    const enOrden = [...frases].sort((una, otra) => ORDEN_DE_LAS_HOJAS[una.hoja] - ORDEN_DE_LAS_HOJAS[otra.hoja] ||
        una.codigo.localeCompare(otra.codigo));
    return enOrden.find((frase) => coincideConLaFrase(normalizado, frase)) ?? null;
}
//# sourceMappingURL=salida-segura.js.map